import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import type { EmojiData, PartialI18n } from "emoji-mart";
import { Picker } from "emoji-mart";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  isWembiiSupportChatActive,
  isWembiiSupportChatDevAccent,
} from "./wembiiSupportChatEnv";
import { isCrispChatEnabled } from "../../../cores/config/featureFlags";
import { useWembiiSupportWidget } from "./useWembiiSupportWidget";
import { WEMBII_SUPPORT_ARTICLES_MOCK } from "./wembiiSupportChatArticlesMock";
import SupportChatHeaderFace from "./SupportChatHeaderFace";
import SupportChatDefaultTeamStack from "./SupportChatDefaultTeamStack";
import {
  resolveSupportChatTheme,
  resolveSupportBrandAvatar,
  resolveSupportMessageAvatar,
} from "./supportChatTheme";
import "emoji-mart/css/emoji-mart.css";
import "./WembiiSupportChat.scss";

type TabKey = "conversation" | "articles";

const CRISP_MARGIN_RIGHT = "calc(20px + 56px + 16px)";
const CRISP_MARGIN_LEFT = "20px";
const STORAGE_KEY_EXPANDED = "wembiiSupportChatExpanded";

const EMOJI_MART_ES: PartialI18n = {
  search: "Busca un emoji...",
  categories: {
    search: "Resultados",
    recent: "Recientes",
    people: "Sonrisas y personas",
    nature: "Animales y naturaleza",
    foods: "Comida y bebida",
    activity: "Actividades",
    places: "Viajes y lugares",
    objects: "Objetos",
    symbols: "Símbolos",
    flags: "Banderas",
    custom: "Personalizado",
  },
};

/** Panel flotante de soporte tipo Crisp — solo UI (mensajería backend en siguiente fase). */
export default function WembiiSupportChat() {
  const { bubbles, sendUserMessage, config, messages, reloadConfig, refreshOnlineAgents, headerAgents } =
    useWembiiSupportWidget();
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [widePanel, setWidePanel] = useState(false);
  const [tab, setTab] = useState<TabKey>("conversation");
  const [quickOpen, setQuickOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiSubTab, setEmojiSubTab] = useState<"emoji" | "gif">("emoji");
  const [articleQuery, setArticleQuery] = useState("");
  const [selectedArticleId, setSelectedArticleId] = useState(
    () => WEMBII_SUPPORT_ARTICLES_MOCK[1]?.id ?? ""
  );
  const [message, setMessage] = useState("");

  const quickRef = useRef<HTMLDivElement>(null);
  const emojiRootRef = useRef<HTMLDivElement>(null);
  const lastNotifiedAgentMessageIdRef = useRef<string | null>(null);
  const agentMessagesInitializedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setLauncherOpen(
        window.localStorage.getItem(STORAGE_KEY_EXPANDED) === "1"
      );
    } catch {
      /* noop */
    }
  }, []);

  const persistLauncher = useCallback((open: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY_EXPANDED, open ? "1" : "0");
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!quickOpen && !emojiOpen) return;

    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        quickOpen &&
        quickRef.current &&
        !quickRef.current.contains(t)
      ) {
        setQuickOpen(false);
      }
      if (
        emojiOpen &&
        emojiRootRef.current &&
        !emojiRootRef.current.contains(t)
      ) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [quickOpen, emojiOpen]);

  const closeLauncher = useCallback(() => {
    setLauncherOpen(false);
    setMinimized(false);
    setWidePanel(false);
    setQuickOpen(false);
    setEmojiOpen(false);
    persistLauncher(false);
  }, [persistLauncher]);

  const openLauncher = useCallback(() => {
    reloadConfig().catch(() => undefined);
    refreshOnlineAgents().catch(() => undefined);
    setLauncherOpen(true);
    setMinimized(false);
    setTab("conversation");
    persistLauncher(true);
  }, [persistLauncher, reloadConfig, refreshOnlineAgents]);

  useEffect(() => {
    const agentMessages = messages.filter((msg) => msg.senderType === "agent");
    const latestAgentMessage = agentMessages[agentMessages.length - 1];
    if (!latestAgentMessage) return;

    if (!agentMessagesInitializedRef.current) {
      agentMessagesInitializedRef.current = true;
      lastNotifiedAgentMessageIdRef.current = latestAgentMessage.id;
      return;
    }

    if (latestAgentMessage.id === lastNotifiedAgentMessageIdRef.current) {
      return;
    }

    lastNotifiedAgentMessageIdRef.current = latestAgentMessage.id;

    if (!launcherOpen || minimized) {
      setLauncherOpen(true);
      setMinimized(false);
      setTab("conversation");
      setQuickOpen(false);
      setEmojiOpen(false);
      persistLauncher(true);
    }
  }, [messages, launcherOpen, minimized, persistLauncher]);

  const onSend = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed) return;
    sendUserMessage(trimmed);
    setMessage("");
    setEmojiOpen(false);
  }, [message, sendUserMessage]);

  const chooseWelcomeConsult = useCallback(
    (label: string) => {
      sendUserMessage(label, label);
    },
    [sendUserMessage]
  );

  const theme = useMemo(() => resolveSupportChatTheme(config), [config]);
  const { appearance, cssVars, panelRoundedClass, bubbleRoundedClass, position } =
    theme;

  const showWelcomeQuickButtons = useMemo(
    () =>
      appearance.showQuickButtons &&
      !bubbles.some((bl) => bl.from === "user"),
    [appearance.showQuickButtons, bubbles]
  );

  const accentForPicker = appearance.accentColor;
  const inputPlaceholder = appearance.inputPlaceholder;
  const welcomeHeadline = appearance.welcomeTitle;
  const onlineLabel = appearance.onlineLabel;
  const widgetTitle = appearance.widgetTitle;
  const showCommercial =
    appearance.showQuickButtons && appearance.showCommercialQuickButton;
  const showTechnical =
    appearance.showQuickButtons && appearance.showTechnicalQuickButton;
  const commercialLabel = appearance.quickButtonCommercial;
  const technicalLabel = appearance.quickButtonTechnical;
  const showAgentName = appearance.showAgentName;
  const showTimestamp = appearance.showTimestamp;

  const resolveAgentAvatar = useCallback(
    (senderAvatar?: string | null) =>
      resolveSupportMessageAvatar(appearance.avatarType, senderAvatar),
    [appearance.avatarType]
  );

  const brandLogoSrc = resolveSupportBrandAvatar(
    appearance.avatarType,
    appearance.avatar
  );
  const wembiiFallbackLogoSrc = resolveSupportBrandAvatar("logo");

  const onEmojiSelect = useCallback((e: EmojiData) => {
    if ("native" in e && e.native) setMessage((prev) => prev + e.native);
  }, []);

  const filteredArticles = useMemo(() => {
    const q = articleQuery.trim().toLowerCase();
    if (!q) return WEMBII_SUPPORT_ARTICLES_MOCK;
    return WEMBII_SUPPORT_ARTICLES_MOCK.filter((a) => {
      const hay = `${a.title} ${a.snippet}`.toLowerCase();
      return hay.includes(q);
    });
  }, [articleQuery]);

  if (!isWembiiSupportChatActive()) return null;
  if (typeof document === "undefined" || !document.body) return null;

  return createPortal(
    <div
      data-wembii-support-chat=""
      className={cn("wembii-support-chat-root", {
        "wembii-support-chat-root--wide": widePanel && launcherOpen,
        "wembii-support-chat-root--left": position === "left",
        "wembii-support-chat-root--solo-fab": !isCrispChatEnabled(),
        "wembii-support-chat-root--dev-accent":
          isWembiiSupportChatDevAccent() && !config,
      })}
      style={{
        ...(cssVars as React.CSSProperties),
        ...(position === "left"
          ? { left: CRISP_MARGIN_LEFT, right: "auto" }
          : { right: CRISP_MARGIN_RIGHT, left: "auto" }),
      }}
    >
      <div className="wsc-launcher-stack">
        {launcherOpen ? (
          <div className="wsc-panel" role="dialog" aria-label={widgetTitle}>
            <div
              className={cn("wsc-panel-inner", panelRoundedClass, {
                "wsc-panel-inner--minimized": minimized,
              })}
            >
              <header className="wsc-header">
                <div className="wsc-header-top">
                  <div className="wsc-tabs-wrap">
                    <div className="wsc-tab-pills">
                      <button
                        type="button"
                        className="wsc-tab-btn"
                        data-active={tab === "conversation"}
                        onClick={() => {
                          setTab("conversation");
                          setMinimized(false);
                        }}
                      >
                        <FontAwesomeIcon icon={["fas", "comment-dots"]} />
                        Conversación
                      </button>
                      <button
                        type="button"
                        className="wsc-tab-btn"
                        data-active={tab === "articles"}
                        onClick={() => {
                          setTab("articles");
                          setMinimized(false);
                        }}
                      >
                        Artículos
                      </button>
                    </div>
                  </div>

                  <div className="wsc-header-controls" ref={quickRef}>
                    {!minimized ? (
                      <button
                        type="button"
                        className="wsc-icon-btn"
                        aria-label="Minimizar"
                        onClick={() => setMinimized(true)}
                      >
                        <FontAwesomeIcon icon={["fas", "chevron-down"]} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="wsc-icon-btn"
                        aria-label="Ampliar"
                        onClick={() => setMinimized(false)}
                      >
                        <FontAwesomeIcon icon={["fas", "chevron-up"]} />
                      </button>
                    )}
                    {!minimized && (
                      <>
                        <button
                          type="button"
                          className="wsc-quick-menu-trigger"
                          onClick={() => setQuickOpen((v) => !v)}
                        >
                          Acciones rápidas
                          <FontAwesomeIcon
                            icon={["fas", quickOpen ? "chevron-up" : "chevron-down"]}
                            style={{ fontSize: 11 }}
                          />
                        </button>
                        {quickOpen && (
                          <div className="wsc-quick-dropdown">
                            <button
                              type="button"
                              onClick={() => {
                                setQuickOpen(false);
                                setMinimized(true);
                              }}
                            >
                              <FontAwesomeIcon icon={["fas", "times"]} />
                              Minimizar el chat
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setWidePanel((w) => !w);
                                setQuickOpen(false);
                              }}
                            >
                              <FontAwesomeIcon icon={["fas", "expand"]} />
                              Ampliar vista
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {tab === "articles" && !minimized ? (
                  <>
                    <div className="wsc-articles-caption">
                      Artículos más populares
                    </div>
                    <label className="wsc-help-search">
                      <FontAwesomeIcon icon={["fas", "search"]} />
                      <input
                        type="search"
                        placeholder="Busca en nuestro centro de ayuda..."
                        autoComplete="off"
                        value={articleQuery}
                        onChange={(e) => setArticleQuery(e.target.value)}
                      />
                    </label>
                  </>
                ) : null}

                {tab === "conversation" && !minimized ? (
                  <>
                    <div className="wsc-team-stack">
                      <div className="wsc-team-stack__faces">
                        {appearance.avatarType === "agent" ? (
                          headerAgents.length ? (
                            headerAgents.map((agent) => (
                              <SupportChatHeaderFace
                                key={agent.userId}
                                agent={agent}
                              />
                            ))
                          ) : (
                            <SupportChatDefaultTeamStack
                              brandLogoSrc={wembiiFallbackLogoSrc}
                            />
                          )
                        ) : (
                          <span
                            className="wsc-face wsc-face--brand"
                            aria-label="Wembii"
                          >
                            <img src={brandLogoSrc} alt="" />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="wsc-headline">{welcomeHeadline}</div>
                    <div className="wsc-subline">
                      <span className="wsc-status-dot" aria-hidden />
                      {onlineLabel}
                    </div>
                  </>
                ) : null}
              </header>

              {!minimized && tab === "articles" ? (
                <section className="wsc-body wsc-body--articles">
                  {filteredArticles.length === 0 ? (
                    <div className="wsc-articles-empty">
                      No encontramos artículos con &ldquo;
                      {articleQuery.trim()}&rdquo;. Probá otra búsqueda.
                    </div>
                  ) : (
                    <ul className="wsc-articles-list" aria-label="Artículos">
                      {filteredArticles.map((article) => (
                        <li key={article.id}>
                          <button
                            type="button"
                            className={cn("wsc-article-row", {
                              "wsc-article-row--selected":
                                selectedArticleId === article.id,
                            })}
                            onClick={() => setSelectedArticleId(article.id)}
                          >
                            <span className="wsc-article-icon" aria-hidden>
                              <FontAwesomeIcon
                                icon={["fas", "book"]}
                                style={{ color: accentForPicker }}
                              />
                            </span>
                            <span className="wsc-article-text">
                              <span className="wsc-article-title">
                                {article.title}
                              </span>
                              <span className="wsc-article-snippet">
                                {article.snippet}
                              </span>
                            </span>
                            <FontAwesomeIcon
                              className="wsc-article-chevron"
                              icon={["fas", "chevron-right"]}
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ) : null}

              {!minimized && tab === "conversation" ? (
                <>
                  <section className="wsc-body">
                    <div className="wsc-msg-list">
                      {bubbles.flatMap((b) => {
                        const rows: JSX.Element[] = [];
                        rows.push(
                          b.from === "agent" ? (
                            <div key={b.id} className="wsc-msg-row">
                              <div
                                className={cn("wsc-msg-avatar", {
                                  "wsc-msg-avatar--photo":
                                    appearance.avatarType === "agent",
                                })}
                              >
                                <img
                                  src={resolveAgentAvatar(b.senderAvatar)}
                                  alt=""
                                />
                              </div>
                              <div className={cn("wsc-bubble", bubbleRoundedClass)}>
                                {showAgentName ? (
                                  <div className="wsc-bubble-sender">
                                    {b.senderName || "Equipo Wembii"}
                                  </div>
                                ) : null}
                                {b.text}
                                {showTimestamp && (b.timestamp || b.isWelcome) ? (
                                  <div className="wsc-bubble-time">
                                    {b.timestamp || "Ahora mismo"}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div key={b.id} className="wsc-msg-row">
                              <div
                                className={cn(
                                  "wsc-bubble",
                                  "wsc-bubble--out",
                                  bubbleRoundedClass
                                )}
                              >
                                {b.text}
                                {showTimestamp && (b.timestamp || b.isWelcome) ? (
                                  <div className="wsc-bubble-time">
                                    {b.timestamp || "Ahora mismo"}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )
                        );
                        if (b.id === "welcome" && showWelcomeQuickButtons) {
                          rows.push(
                            <div
                              key="welcome-quick-buttons"
                              className="wsc-msg-row wsc-msg-row--quick-actions"
                            >
                              <span
                                className="wsc-msg-avatar-spacer"
                                aria-hidden
                              />
                              <div className="wsc-welcome-quick-buttons">
                                {showCommercial && (
                                  <button
                                    type="button"
                                    className="wsc-chip-btn"
                                    title={commercialLabel}
                                    onClick={() =>
                                      chooseWelcomeConsult(commercialLabel)
                                    }
                                  >
                                    {commercialLabel}
                                  </button>
                                )}
                                {showTechnical && (
                                  <button
                                    type="button"
                                    className="wsc-chip-btn"
                                    title={technicalLabel}
                                    onClick={() =>
                                      chooseWelcomeConsult(technicalLabel)
                                    }
                                  >
                                    {technicalLabel}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return rows;
                      })}
                    </div>
                  </section>

                  <footer className="wsc-foot">
                    <div ref={emojiRootRef}>
                      {emojiOpen && (
                        <div className="wsc-emoji-popover">
                          <div className="wsc-emoji-slot">
                            {emojiSubTab === "emoji" ? (
                              <Picker
                                native
                                color={accentForPicker}
                                sheetSize={32}
                                showPreview
                                showSkinTones
                                exclude={["recent"]}
                                onSelect={onEmojiSelect}
                                i18n={EMOJI_MART_ES}
                                style={{
                                  border: "none",
                                  boxShadow: "none",
                                  width: "100%",
                                  height: 246,
                                  maxHeight: 246,
                                }}
                              />
                            ) : (
                              <div className="wsc-emoji-gifs">
                                GIFs disponibles próximamente en el widget
                                gestionado.
                              </div>
                            )}
                          </div>
                          <div className="wsc-emoji-toggle-row">
                            <button
                              type="button"
                              className="wsc-emoji-toggle"
                              data-on={emojiSubTab === "emoji"}
                              onClick={() => setEmojiSubTab("emoji")}
                            >
                              <FontAwesomeIcon icon={["fas", "face-smile"]} />{" "}
                              Emoticonos
                            </button>
                            <button
                              type="button"
                              className="wsc-emoji-toggle"
                              data-on={emojiSubTab === "gif"}
                              onClick={() => setEmojiSubTab("gif")}
                            >
                              GIFs
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="wsc-composer-shell">
                        <div className="wsc-inline-tools">
                          <button
                            type="button"
                            aria-label="Emojis"
                            onClick={() =>
                              setEmojiOpen((prev) => {
                                const next = !prev;
                                if (next) setEmojiSubTab("emoji");
                                return next;
                              })
                            }
                          >
                            <FontAwesomeIcon icon={["fas", "face-smile"]} />
                          </button>
                          <button
                            type="button"
                            aria-label="Adjuntar archivo"
                            disabled
                            className="wsc-inline-tools__disabled"
                            title="Adjuntos (próximamente)"
                          >
                            <FontAwesomeIcon icon={["fas", "paperclip"]} />
                          </button>
                          <button
                            type="button"
                            aria-label="Mensaje de voz"
                            disabled
                            className="wsc-inline-tools__disabled"
                            title="Audio (próximamente)"
                          >
                            <FontAwesomeIcon icon={["fas", "microphone"]} />
                          </button>
                        </div>

                        <div className="wsc-composer-input-row">
                          <textarea
                            rows={1}
                            placeholder={inputPlaceholder}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Enter" &&
                                !e.shiftKey
                              ) {
                                e.preventDefault();
                                onSend();
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="wsc-send"
                            aria-label="Enviar"
                            disabled={!message.trim()}
                            onClick={onSend}
                          >
                            <FontAwesomeIcon icon={["fas", "paper-plane"]} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </footer>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {launcherOpen ? (
          <button
            type="button"
            className="wsc-fab"
            aria-label={launcherOpen ? "Cerrar soporte" : widgetTitle}
            onClick={closeLauncher}
          >
            <FontAwesomeIcon icon={["fas", "times"]} size="lg" />
          </button>
        ) : (
          <button
            type="button"
            className="wsc-fab"
            aria-label={widgetTitle}
            onClick={openLauncher}
          >
            <FontAwesomeIcon icon={["fas", "comment-dots"]} size="lg" />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
